import { useT } from "../../i18n/index.js";
import {
  deltaColorClass,
  formatDays,
  formatMoney,
  formatPctSigned,
  toNumber,
} from "../../lib/format-fr.js";
import type { AppRouter } from "../../lib/trpc.js";
import type { inferRouterOutputs } from "@trpc/server";

type ForecastRow = NonNullable<inferRouterOutputs<AppRouter>["fiscalForecasts"]["getByFiscalYear"]>;

/**
 * Painel "Projection de Clôture" do dashboard fiscal.
 *
 * Renderiza tabela comparativa de 7 KPIs: Projection vs Réel N-1, com
 * variação pct quando baseline disponível. Não renderiza nada quando
 * `forecast === null` — a página decide o fallback (botão "Actualiser
 * projection" + skeleton de loading).
 */
export function ForecastPanel({
  forecast,
  fiscalYearLabel,
  baselineYearLabel,
  onRecompute,
  isRecomputing,
}: {
  forecast: ForecastRow;
  fiscalYearLabel: string;
  baselineYearLabel: string | null;
  onRecompute?: () => void;
  isRecomputing?: boolean;
}) {
  const t = useT();
  const IS_STRATEGY_LABELS: Record<string, string> = {
    lancements: t("forecastPanel.isStrategyLancements"),
    taxe_fr_25: t("forecastPanel.isStrategyTaxeFr25"),
    none: t("forecastPanel.isStrategyNone"),
  };
  const hasN1 = forecast.baselineFiscalYearId !== null;
  const moisEcoules = Math.round(Number(forecast.moisEcoules));
  const moisRestantes = Math.round(Number(forecast.moisRestantes));
  const factor = Number(forecast.projectionFactor);

  type RowDef = {
    label: string;
    proj: string | null;
    n1: string | null;
    variation: string | null;
    suffix?: string;
    tone?: "alert" | "warn" | "normal";
  };

  const rnProj = toNumber(forecast.resultatNetProjete) ?? 0;
  const isEstime = toNumber(forecast.isEstime) ?? 0;
  const caProj = toNumber(forecast.caProjete) ?? 0;
  const seuilProj = toNumber(forecast.seuilEconomiqueProjete);

  const rows: RowDef[] = [
    {
      label: t("forecastPanel.rowChiffreAffaires"),
      proj: forecast.caProjete,
      n1: forecast.n1Ca,
      variation: forecast.varCaPct,
      tone: seuilProj !== null && caProj < seuilProj ? "alert" : "normal",
    },
    {
      label: t("forecastPanel.rowMargeGlobale"),
      proj: forecast.margeProjetee,
      n1: forecast.n1Marge,
      variation: forecast.varMargePct,
    },
    {
      label: t("forecastPanel.rowResultatNet"),
      proj: forecast.resultatNetProjete,
      n1: forecast.n1ResultatNet,
      variation: forecast.varRnPct,
      tone: rnProj < 0 ? "alert" : "normal",
    },
    {
      label: t("forecastPanel.rowIsEstime"),
      proj: forecast.isEstime,
      n1: forecast.n1Is,
      variation: forecast.varIsPct,
      tone: isEstime > 5000 ? "warn" : "normal",
    },
    {
      label: t("forecastPanel.rowCaf"),
      proj: forecast.cafProjetee,
      n1: forecast.n1Caf,
      variation: null,
    },
    {
      label: t("forecastPanel.rowTresorerieEstimee"),
      proj: forecast.tresorerieEstimee,
      n1: forecast.n1Tresorerie,
      variation: null,
      tone:
        toNumber(forecast.tresorerieEstimee) !== null &&
        (toNumber(forecast.tresorerieEstimee) ?? 0) < 0
          ? "alert"
          : "normal",
    },
    {
      label: t("forecastPanel.rowPointMort"),
      proj: forecast.pointMortProjete,
      n1: forecast.n1PointMort,
      variation: null,
      suffix: "j",
    },
  ];

  return (
    <section className="rounded-lg border border-sky-500/30 bg-sky-500/[0.03] overflow-hidden">
      <div className="px-5 py-3 border-b border-sky-500/20 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-sky-200">
            {t("forecastPanel.title")} — {fiscalYearLabel}
          </h2>
          <p className="text-xs text-neutral-400 mt-0.5">
            {t("forecastPanel.linearTrend")} · {moisEcoules} {t("forecastPanel.monthsActual")} ·{" "}
            {moisRestantes} {t("forecastPanel.monthsProjected")} · {t("forecastPanel.factorLabel")}{" "}
            ×{factor.toFixed(3)} · {t("forecastPanel.isStrategyLabel")}{" "}
            <span className="text-sky-300">
              {IS_STRATEGY_LABELS[forecast.isStrategyApplied] ?? forecast.isStrategyApplied}
            </span>
          </p>
        </div>
        {onRecompute && (
          <button
            onClick={onRecompute}
            disabled={isRecomputing}
            className="rounded-md border border-sky-500/40 hover:border-sky-500 hover:text-sky-200 text-sky-300 px-3 py-1.5 text-xs disabled:opacity-50"
          >
            {isRecomputing
              ? t("forecastPanel.recomputing")
              : `↻ ${t("forecastPanel.recomputeBtn")}`}
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-neutral-500">
            <tr className="border-b border-neutral-800">
              <th className="text-left px-5 py-2">{t("forecastPanel.colKpi")}</th>
              <th className="text-right px-3 py-2 font-mono">
                {t("forecastPanel.colProjection")} {fiscalYearLabel}
              </th>
              <th className="text-right px-3 py-2 font-mono">
                {t("forecastPanel.colRealN1")}
                {baselineYearLabel ? ` (${baselineYearLabel})` : ""}
              </th>
              <th className="text-right px-3 py-2 font-mono w-32">
                {t("forecastPanel.colVariation")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-900">
            {rows.map((r) => {
              const toneCls =
                r.tone === "alert"
                  ? "text-red-300"
                  : r.tone === "warn"
                    ? "text-amber-300"
                    : "text-neutral-100";
              const projDisplay = r.suffix === "j" ? formatDays(r.proj) : formatMoney(r.proj);
              const n1Display = !hasN1
                ? "—"
                : r.suffix === "j"
                  ? formatDays(r.n1)
                  : formatMoney(r.n1);
              return (
                <tr key={r.label}>
                  <td className="px-5 py-2 text-neutral-200">{r.label}</td>
                  <td className={`px-3 py-2 text-right font-mono tabular-nums ${toneCls}`}>
                    {projDisplay}
                    {r.tone === "warn" && <span className="ml-1">⚠</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-400">
                    {n1Display}
                  </td>
                  <td
                    className={`px-3 py-2 text-right font-mono tabular-nums ${deltaColorClass(
                      r.variation,
                    )}`}
                  >
                    {!hasN1 || r.variation === null
                      ? "—"
                      : formatPctSigned(r.variation, { alreadyPct: true })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-5 py-2 border-t border-neutral-800 text-[11px] text-neutral-500">
        {t("forecastPanel.computedAt")}{" "}
        {forecast.computedAt ? new Date(forecast.computedAt).toLocaleString("fr-FR") : "—"}
      </div>
    </section>
  );
}
